import { useMemo, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOpportunityCategories } from "@/hooks/queries";
import {
  useGenreMappings,
  useArtistTypeMappings,
  usePollstarStatus,
  usePollstarSchedule,
} from "@/hooks/queries/usePollstar";
import { useNotification } from "@/hooks/useNotification";
import {
  apiMapGenre,
  apiBulkMapGenres,
  apiMapArtistType,
  apiUpdateSyncSchedule,
  apiTriggerBackfill,
  apiTriggerRebuild,
  apiPreviewGenreRemap,
  type PollstarBusinessType,
  type GenreMapping,
} from "@api/pollstar";
import styles from "./styles.module.css";

// "Ignore" excludes a genre/artist-type's events from derivation entirely (no row in any category) —
// distinct from an unmapped value, which falls through to the visible Concert/Other catch-all.
const BUSINESS_TYPES: PollstarBusinessType[] = ["Concert", "Theater", "Corporate", "Ignore"];

const formatTs = (ts: string | null): string =>
  ts ? new Date(ts).toLocaleString() : "—";

export const PollstarSection = () => {
  const [tab, setTab] = useState(0);
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Pollstar</h2>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Genre Mappings" />
        <Tab label="Artist Types" />
        <Tab label="Sync & Status" />
      </Tabs>
      {tab === 0 && <GenreTab />}
      {tab === 1 && <ArtistTypeTab />}
      {tab === 2 && <SyncStatusTab />}
    </div>
  );
};

type GenreEdit = { bt: PollstarBusinessType; sub: string | null };

const GenreTab = () => {
  const [showAll, setShowAll] = useState(false);
  const { data: mappings = [], isLoading } = useGenreMappings(showAll ? undefined : "Pending");
  const { data: categories = [] } = useOpportunityCategories();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();

  const subcategoryNames = useMemo(() => {
    const concert = categories.find((c) => c.name.startsWith("Concert Tours"));
    return concert ? concert.subcategories.map((s) => s.name) : [];
  }, [categories]);

  const [edits, setEdits] = useState<Record<string, GenreEdit>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBt, setBulkBt] = useState<PollstarBusinessType>("Concert");
  const [bulkSub, setBulkSub] = useState<string>("");
  const [confirm, setConfirm] = useState<{ count: number; run: () => void } | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["pollstarGenreMappings"] });
    queryClient.invalidateQueries({ queryKey: ["pollstarPendingCount"] });
    queryClient.invalidateQueries({ queryKey: ["pollstarStatus"] });
  };

  const editOf = (m: GenreMapping): GenreEdit =>
    edits[m.id] ?? { bt: m.business_type === "Other" ? "Concert" : m.business_type, sub: m.subcategory_name };

  const setEdit = (m: GenreMapping, patch: Partial<GenreEdit>) =>
    setEdits((prev) => ({ ...prev, [m.id]: { ...editOf(m), ...patch } }));

  const mapMutation = useMutation({
    mutationFn: ({ id, bt, sub }: { id: string } & GenreEdit) =>
      apiMapGenre(id, bt, bt === "Concert" ? sub : null),
    onSuccess: () => { invalidate(); notifySuccess("Mapping saved"); },
    onError: () => notifyError("Failed to save mapping"),
  });

  const bulkMutation = useMutation({
    mutationFn: ({ ids, bt, sub }: { ids: string[] } & GenreEdit) =>
      apiBulkMapGenres(ids, bt, bt === "Concert" ? sub : null),
    onSuccess: (n) => { invalidate(); setSelected(new Set()); notifySuccess(`Mapped ${n} genre(s)`); },
    onError: () => notifyError("Failed to bulk-map"),
  });

  const saveRow = async (m: GenreMapping) => {
    const edit = editOf(m);
    const run = () => mapMutation.mutate({ id: m.id, ...edit });
    try {
      const count = await apiPreviewGenreRemap(m.id);
      if (count > 0) setConfirm({ count, run });
      else run();
    } catch {
      run();
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Map Pollstar genres to a CL business segment (and concert subcategory). Unmapped genres default
          to Concert / Other until set here.
        </Typography>
        <FormControlLabel
          sx={{ flexShrink: 0, mr: 0, "& .MuiFormControlLabel-label": { whiteSpace: "nowrap" } }}
          control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label="Show all"
        />
      </Box>

      {selected.size > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="body2">{selected.size} selected →</Typography>
          <TextField select size="small" value={bulkBt} onChange={(e) => setBulkBt(e.target.value as PollstarBusinessType)} sx={{ minWidth: 120 }}>
            {BUSINESS_TYPES.map((bt) => <MenuItem key={bt} value={bt}>{bt}</MenuItem>)}
          </TextField>
          {bulkBt === "Concert" && (
            <TextField select size="small" value={bulkSub} onChange={(e) => setBulkSub(e.target.value)} label="Subcategory" sx={{ minWidth: 160 }}>
              <MenuItem value=""><em>None</em></MenuItem>
              {subcategoryNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
          )}
          <Button size="small" variant="contained" onClick={() => bulkMutation.mutate({ ids: [...selected], bt: bulkBt, sub: bulkSub || null })} disabled={bulkMutation.isPending}>
            Apply to {selected.size}
          </Button>
        </Box>
      )}

      {isLoading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : mappings.length === 0 ? (
        <Typography color="text.secondary">No {showAll ? "" : "pending "}genre mappings.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Pollstar Genre</TableCell>
              <TableCell>Segment</TableCell>
              <TableCell>Subcategory</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((m) => {
              const edit = editOf(m);
              return (
                <TableRow key={m.id}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
                  </TableCell>
                  <TableCell>{m.pollstar_value}</TableCell>
                  <TableCell>
                    <TextField select size="small" value={edit.bt} onChange={(e) => setEdit(m, { bt: e.target.value as PollstarBusinessType })} sx={{ minWidth: 120 }}>
                      {BUSINESS_TYPES.map((bt) => <MenuItem key={bt} value={bt}>{bt}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select size="small"
                      value={edit.sub ?? ""}
                      onChange={(e) => setEdit(m, { sub: e.target.value || null })}
                      disabled={edit.bt !== "Concert"}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {subcategoryNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={m.status}
                      color={m.status === "Pending" ? "warning" : "success"}
                      variant={m.status === "Pending" ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="contained" onClick={() => saveRow(m)} disabled={mapMutation.isPending}>
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        <DialogTitle>Re-map genre</DialogTitle>
        <DialogContent>
          <Typography>
            This will re-derive {confirm?.count} opportunity(ies) currently derived from this genre.
            Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => { confirm?.run(); setConfirm(null); }}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const ArtistTypeTab = () => {
  const [showAll, setShowAll] = useState(false);
  const { data: mappings = [], isLoading } = useArtistTypeMappings(showAll ? undefined : "Pending");
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();
  const [edits, setEdits] = useState<Record<string, PollstarBusinessType>>({});

  const mapMutation = useMutation({
    mutationFn: ({ id, bt }: { id: string; bt: PollstarBusinessType }) => apiMapArtistType(id, bt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pollstarArtistTypeMappings"] });
      queryClient.invalidateQueries({ queryKey: ["pollstarPendingCount"] });
      notifySuccess("Mapping saved");
    },
    onError: () => notifyError("Failed to save mapping"),
  });

  return (
    <div>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Artist types are a fallback segment signal when a genre is unmapped (e.g. Performing Arts → Theater).
        </Typography>
        <FormControlLabel
          sx={{ flexShrink: 0, mr: 0, "& .MuiFormControlLabel-label": { whiteSpace: "nowrap" } }}
          control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label="Show all"
        />
      </Box>
      {isLoading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : mappings.length === 0 ? (
        <Typography color="text.secondary">No {showAll ? "" : "pending "}artist-type mappings.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pollstar Artist Type</TableCell>
              <TableCell>Segment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((m) => {
              const bt = edits[m.id] ?? (m.business_type === "Other" ? "Concert" : m.business_type);
              return (
                <TableRow key={m.id}>
                  <TableCell>{m.pollstar_value}</TableCell>
                  <TableCell>
                    <TextField select size="small" value={bt} onChange={(e) => setEdits((p) => ({ ...p, [m.id]: e.target.value as PollstarBusinessType }))} sx={{ minWidth: 120 }}>
                      {BUSINESS_TYPES.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={m.status} color={m.status === "Pending" ? "warning" : "success"} variant={m.status === "Pending" ? "filled" : "outlined"} />
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="contained" onClick={() => mapMutation.mutate({ id: m.id, bt })} disabled={mapMutation.isPending}>
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

const SyncStatusTab = () => {
  const { data: status } = usePollstarStatus();
  const { data: schedule } = usePollstarSchedule();
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useNotification();

  const [runsPerDay, setRunsPerDay] = useState<number | null>(null);
  const [startHour, setStartHour] = useState<number | null>(null);
  const [timeZone, setTimeZone] = useState<string | null>(null);
  const [gapDays, setGapDays] = useState<number | null>(null);

  const rpd = runsPerDay ?? schedule?.runs_per_day ?? 1;
  const sh = startHour ?? schedule?.start_hour ?? 3;
  const tz = timeZone ?? schedule?.time_zone ?? "America/New_York";
  const gd = gapDays ?? schedule?.gap_threshold_days ?? 21;

  const scheduleMutation = useMutation({
    mutationFn: () => apiUpdateSyncSchedule(rpd, sh, tz, gd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pollstarSyncSchedule"] });
      notifySuccess("Schedule saved");
    },
    onError: () => notifyError("Failed to save schedule"),
  });

  const backfillMutation = useMutation({
    mutationFn: apiTriggerBackfill,
    onSuccess: () => notifySuccess("Backfill started"),
    onError: () => notifyError("Backfill could not be started (one may be running)"),
  });

  const rebuildMutation = useMutation({
    mutationFn: apiTriggerRebuild,
    onSuccess: () => notifySuccess("Full re-derivation enqueued"),
    onError: () => notifyError("Failed to enqueue rebuild"),
  });

  const runTimes = useMemo(() => {
    const spacing = Math.floor(24 / Math.min(Math.max(rpd, 1), 4));
    return Array.from({ length: rpd }, (_, i) => `${String((sh + i * spacing) % 24).padStart(2, "0")}:00`);
  }, [rpd, sh]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Status</Typography>
        <Typography variant="body2">Pending mappings: <strong>{status?.pending_count ?? 0}</strong></Typography>
        <Typography variant="body2">Last sync: {formatTs(status?.last_sync_at ?? null)}</Typography>
        <Typography variant="body2">Last backfill: {formatTs(status?.last_backfill_at ?? null)}</Typography>
        <Typography variant="body2">Last derivation: {formatTs(status?.last_derivation_at ?? null)}</Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button size="small" variant="outlined" onClick={() => backfillMutation.mutate()} disabled={backfillMutation.isPending}>
            Run Backfill
          </Button>
          <Button size="small" variant="outlined" onClick={() => rebuildMutation.mutate()} disabled={rebuildMutation.isPending}>
            Rebuild All
          </Button>
        </Box>
      </Box>

      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Sync Schedule</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField select size="small" label="Runs/day" value={rpd} onChange={(e) => setRunsPerDay(Number(e.target.value))} sx={{ minWidth: 100 }}>
            {[1, 2, 3, 4].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Start hour" value={sh} onChange={(e) => setStartHour(Number(e.target.value))} sx={{ minWidth: 100 }}>
            {Array.from({ length: 24 }, (_, h) => <MenuItem key={h} value={h}>{String(h).padStart(2, "0")}:00</MenuItem>)}
          </TextField>
          <TextField size="small" label="Time zone" value={tz} onChange={(e) => setTimeZone(e.target.value)} sx={{ minWidth: 200 }} />
          <TextField
            size="small"
            type="number"
            label="Leg split (days)"
            value={gd}
            onChange={(e) => setGapDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 365 }}
            sx={{ minWidth: 140 }}
          />
          <Button size="small" variant="contained" onClick={() => scheduleMutation.mutate()} disabled={scheduleMutation.isPending}>
            Save Schedule
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Runs at: {runTimes.join(", ")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Leg split: a gap larger than {gd} day{gd === 1 ? "" : "s"} between an artist's shows starts a new
          opportunity row. Changing this value triggers a full re-derivation of all opportunities.
        </Typography>
      </Box>

      {status && status.recent_failures.length > 0 && (
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Recent failures</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Artist</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status.recent_failures.map((f, i) => (
                <TableRow key={i}>
                  <TableCell>{formatTs(f.ran_at)}</TableCell>
                  <TableCell>{f.artist_id ?? "—"}</TableCell>
                  <TableCell>{f.trigger ?? "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 360, whiteSpace: "normal" }}>{f.error ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
